import { useState, useEffect, useRef, useCallback } from 'react';
import {
    collection, query, where, orderBy, limit, startAfter, getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';
import { SOCIAL_PAGE_SIZE, SPORTS } from '../data/constants';
import Friends from './Friends';
import { beltLabel } from './BjjPromotionSummary';
import { calcStreak } from '../utils/streak';

function timeAgo(ts) {
    if (!ts) return '';
    const seconds = Math.floor(Date.now() / 1000 - (ts.seconds || 0));
    if (seconds < 60) return '방금 전';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}분 전`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}시간 전`;
    if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}일 전`;
    return ts.toDate ? ts.toDate().toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }) : '';
}

function fmtDuration(min) {
    const total = +min || 0;
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h && m) return `${h}시간 ${m}분`;
    if (h) return `${h}시간`;
    return `${m}분`;
}

function PostCard({ post, myUid, onLike, onProfile }) {
    const sp = SPORTS[post.sport];
    const isLiked = !!(post.likes && post.likes[myUid]);
    const techniques = (post.techniques || []).filter(t => t.name);
    const exercises = (post.exercises || []).filter(ex => ex.name);
    const sportName = sp?.name || post.sport || '운동';
    const headline = post.postMessage?.trim() || `${sportName} 완료했어요!`;
    const highlights = [
        post.duration && ['운동 시간', fmtDuration(post.duration)],
        post.sparring && ['스파링', post.sparringRounds ? `${post.sparringRounds}R` : '완료'],
        post.distance && ['거리', `${post.distance}km`],
        post.avgHR && ['평균 심박', `${post.avgHR}bpm`],
        post.power && ['파워', `${post.power}W`],
    ].filter(Boolean).slice(0, 3);

    if (post.type === 'promotion') {
        return (
            <div className="social-post-card promotion-post-card">
                <div className="post-head">
                    <button className="profile-open-btn" onClick={() => onProfile(post)} aria-label="프로필 보기">
                        <div className="friend-avatar post-avatar">{post.nickname?.slice(0, 1) || '?'}</div>
                    </button>
                    <button className="post-author-btn" onClick={() => onProfile(post)}>
                        <div className="post-author">{post.nickname}</div>
                        <div className="post-meta">@{post.userId} · {timeAgo(post.createdAt)}</div>
                    </button>
                    <div className="post-visibility-badge">
                        {post.visibility === 'public' ? '전체' : post.visibility === 'friends' ? '친구' : '나만'}
                    </div>
                </div>
                <div className="promotion-post-title">주짓수 승급</div>
                <div className="promotion-post-belt">{beltLabel(post.belt)} {+post.stripe || 0}그랄로 승급했어요! 🎉</div>
                <div className="post-stats-row">
                    {post.date && <span className="tag">{post.date}</span>}
                    {post.place && <span className="tag">{post.place}</span>}
                </div>
                {post.postMessage && <div className="post-focus">{post.postMessage}</div>}
                <div className="post-actions">
                    <button className={`like-btn ${isLiked ? 'liked' : ''}`} onClick={() => onLike(post.id, isLiked)}>
                        <svg viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                        </svg>
                        <span>{post.likeCount || 0}</span>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="social-post-card">
            <div className="post-head">
                <button className="profile-open-btn" onClick={() => onProfile(post)} aria-label="프로필 보기">
                    <div className="friend-avatar post-avatar">{post.nickname?.slice(0, 1) || '?'}</div>
                </button>
                <button className="post-author-btn" onClick={() => onProfile(post)}>
                    <div className="post-author">{post.nickname}</div>
                    <div className="post-meta">@{post.userId} · {timeAgo(post.createdAt)}</div>
                </button>
                <div className="post-visibility-badge">
                    {post.visibility === 'public' ? '전체' : post.visibility === 'friends' ? '친구' : '나만'}
                </div>
            </div>

            <div className="post-hero">
                <div className="post-sport-row">
                    <div className="sport-dot" style={{ background: sp?.color || '#888' }} />
                    <span className="post-sport-name">{sportName}</span>
                    <span className="post-date-label">{post.date}</span>
                </div>
                <div className="post-headline">{headline}</div>
                {post.focus && <div className="post-focus subtle">오늘의 목표 · {post.focus}</div>}
            </div>

            {highlights.length > 0 && (
                <div className="post-highlight-grid">
                    {highlights.map(([label, value]) => (
                        <div key={label}>
                            <span>{label}</span>
                            <strong>{value}</strong>
                        </div>
                    ))}
                </div>
            )}

            <div className="post-stats-row post-mood-row">
                {post.condScore && <span className="tag">컨디션 {post.condScore}/5</span>}
                {post.fatScore && <span className="tag">강도 {post.fatScore}/5</span>}
                {post.pace && <span className="tag">{post.pace}/km</span>}
                {(post.muscleGroups || []).map(m => <span key={m} className="tag">{m}</span>)}
            </div>

            {techniques.length > 0 && (
                <div className="post-section-block">
                    <div className="post-section-label">기술 메모</div>
                    <div className="post-chips">
                        {techniques.slice(0, 3).map((t, i) => (
                            <span key={i} className="post-chip">
                                {t.name}
                                {t.result && <span className={`chip-result chip-result-${t.result}`}>{t.result}</span>}
                                {t.category && <span className="chip-cat">{t.category}</span>}
                            </span>
                        ))}
                        {techniques.length > 3 && <span className="post-chip chip-more">+{techniques.length - 3}</span>}
                    </div>
                    {techniques.find(t => t.point)?.point && (
                        <div className="post-note-text">{techniques.find(t => t.point).point}</div>
                    )}
                </div>
            )}

            {exercises.length > 0 && (
                <div className="post-section-block">
                    <div className="post-section-label">오늘 한 운동</div>
                    <div className="post-chips">
                        {exercises.slice(0, 3).map((ex, i) => (
                            <span key={i} className="post-chip">
                                {ex.name}
                                {ex.muscleGroup && <span className="chip-cat">{ex.muscleGroup}</span>}
                            </span>
                        ))}
                        {exercises.length > 3 && <span className="post-chip chip-more">+{exercises.length - 3}</span>}
                    </div>
                </div>
            )}

            {post.strikingDrills?.filter(d => d.type).length > 0 && (
                <div className="post-section-block">
                    <div className="post-section-label">훈련 구성</div>
                    <div className="post-chips">
                        {post.strikingDrills.filter(d => d.type).slice(0, 3).map((d, i) => (
                            <span key={i} className="post-chip">
                                {d.type}
                                {d.minutes && <span className="chip-cat">{d.minutes}분</span>}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="post-actions">
                <button
                    className={`like-btn ${isLiked ? 'liked' : ''}`}
                    onClick={() => onLike(post.id, isLiked)}
                >
                    <svg viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                    </svg>
                    <span>{post.likeCount || 0}</span>
                </button>
            </div>
        </div>
    );
}

function ProfileModal({ profile, myUid, friends, getFriendData, addFriend, showToast, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);

    const isMe = profile.uid === myUid;
    const isFriend = (friends || []).some(f => f.uid === profile.uid);

    useEffect(() => {
        let alive = true;
        if (!profile.uid) return undefined;
        setLoading(true);
        getFriendData(profile.uid)
            .then(d => { if (alive) setData(d); })
            .catch(() => { if (alive) setData(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [profile.uid, isMe, getFriendData]);

    const records = data?.records || [];
    const totalMin = records.reduce((sum, r) => sum + (+r.duration || 0), 0);
    const recent = records
        .filter(r => r.visibility !== 'private')
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 3);

    const handleAdd = async () => {
        if (!profile.userId || isMe || isFriend) return;
        setAdding(true);
        try {
            await addFriend(profile.userId, friends);
            showToast('친구 추가 완료!');
        } catch (e) {
            const msgs = { not_found: '존재하지 않는 ID예요', self: '본인은 추가할 수 없어요', already: '이미 친구예요' };
            showToast(msgs[e.message] || '친구 추가에 실패했어요');
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="confirm-backdrop" onClick={onClose}>
            <div className="confirm-panel social-profile-panel" onClick={e => e.stopPropagation()}>
                <div className="social-profile-head">
                    <div className="friend-avatar social-profile-avatar">{profile.nickname?.slice(0, 1) || '?'}</div>
                    <div>
                        <div className="social-profile-name">{profile.nickname || '사용자'}</div>
                        <div className="social-profile-id">@{profile.userId || 'unknown'}</div>
                    </div>
                </div>

                {loading ? (
                    <div className="empty-state"><div className="empty-text">···</div></div>
                ) : (
                    <>
                        <div className="social-profile-stats">
                            <div><strong>{records.length}개</strong><span>기록</span></div>
                            <div><strong>{calcStreak(records)}일</strong><span>연속 운동일 수</span></div>
                            <div><strong>{Math.round(totalMin / 60) || 0}h</strong><span>총 시간</span></div>
                        </div>
                        {recent.length > 0 && (
                            <div className="social-profile-recent">
                                {recent.map((r, i) => (
                                    <div key={i} className="social-profile-record">
                                        <span>{SPORTS[r.sport]?.name || r.sport}</span>
                                        <em>{r.date}</em>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                <div className="confirm-actions">
                    <button className="action-btn" onClick={onClose}>닫기</button>
                    {!isMe && !isFriend && (
                        <button className="action-btn" onClick={handleAdd} disabled={adding}>
                            {adding ? '친구 추가 요청됨' : '친구 추가'}
                        </button>
                    )}
                    {/* {!isMe && isFriend && <button className="action-btn" disabled>우리는 이미 친구예요!</button>} */}
                </div>
            </div>
        </div>
    );
}

function FeedTab({ uid, nickname, friends, toggleLike, onProfile, showToast }) {
    const [posts, setPosts] = useState([]);
    const [filter, setFilter] = useState('all');
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const cursorPublic = useRef(null);
    const cursorFriends = useRef(null);
    const isLoadingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const sentinelRef = useRef(null);

    useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

    const fetchMore = useCallback(async (reset = false) => {
        if (isLoadingRef.current || (!reset && !hasMoreRef.current)) return;
        isLoadingRef.current = true;
        setLoading(true);

        try {
            const postsCol = collection(db, 'posts');

            let q1 = query(postsCol, where('visibility', '==', 'public'), orderBy('createdAt', 'desc'), limit(SOCIAL_PAGE_SIZE));
            if (!reset && cursorPublic.current) q1 = query(q1, startAfter(cursorPublic.current));

            let q2 = query(postsCol, where('visibleUids', 'array-contains', uid), orderBy('createdAt', 'desc'), limit(SOCIAL_PAGE_SIZE));
            if (!reset && cursorFriends.current) q2 = query(q2, startAfter(cursorFriends.current));

            const [publicResult, friendsResult] = await Promise.allSettled([getDocs(q1), getDocs(q2)]);
            if (publicResult.status === 'rejected') console.error('전체 공개 피드 로드 오류:', publicResult.reason);
            if (friendsResult.status === 'rejected') console.error('친구 공개 피드 로드 오류:', friendsResult.reason);

            let usedFallback = false;
            let publicDocs = publicResult.status === 'fulfilled' ? publicResult.value.docs : [];
            let friendDocs = friendsResult.status === 'fulfilled' ? friendsResult.value.docs : [];

            if (publicResult.status === 'rejected') {
                usedFallback = true;
                try {
                    const fallback = await getDocs(query(postsCol, where('visibility', '==', 'public'), limit(SOCIAL_PAGE_SIZE)));
                    publicDocs = fallback.docs;
                } catch (err) {
                    console.error('전체 공개 피드 fallback 오류:', err);
                }
            }
            if (friendsResult.status === 'rejected') {
                usedFallback = true;
                try {
                    const fallback = await getDocs(query(postsCol, where('visibleUids', 'array-contains', uid), limit(SOCIAL_PAGE_SIZE)));
                    friendDocs = fallback.docs;
                } catch (err) {
                    console.error('친구 공개 피드 fallback 오류:', err);
                }
            }

            if (publicDocs.length > 0) cursorPublic.current = publicDocs[publicDocs.length - 1];
            if (friendDocs.length > 0) cursorFriends.current = friendDocs[friendDocs.length - 1];

            const newHasMore = !usedFallback && (publicDocs.length === SOCIAL_PAGE_SIZE || friendDocs.length === SOCIAL_PAGE_SIZE);
            setHasMore(newHasMore);
            hasMoreRef.current = newHasMore;

            setPosts(prev => {
                const existingIds = new Set(reset ? [] : prev.map(p => p.id));
                const merged = [];
                [...publicDocs, ...friendDocs].forEach(d => {
                    if (!existingIds.has(d.id)) {
                        existingIds.add(d.id);
                        merged.push({ id: d.id, ...d.data() });
                    }
                });
                merged.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
                return reset ? merged : [...prev, ...merged];
            });
        } catch (err) {
            console.error('피드 로드 오류:', err);
            showToast?.('피드를 불러오지 못했어요');
        } finally {
            isLoadingRef.current = false;
            setLoading(false);
        }
    }, [uid, showToast]);

    useEffect(() => { fetchMore(true); }, [fetchMore]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const obs = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && !isLoadingRef.current && hasMoreRef.current) {
                fetchMore();
            }
        }, { threshold: 0.1 });
        obs.observe(sentinel);
        return () => obs.disconnect();
    }, [fetchMore]);

    const handleLike = async (postId, isLiked) => {
        setPosts(prev => prev.map(p => {
            if (p.id !== postId) return p;
            const likes = { ...(p.likes || {}) };
            if (isLiked) {
                delete likes[uid];
            } else {
                likes[uid] = { nickname: nickname || '', likedAt: { seconds: Date.now() / 1000 } };
            }
            return { ...p, likes, likeCount: Math.max(0, (p.likeCount || 0) + (isLiked ? -1 : 1)) };
        }));

        try {
            await toggleLike(postId, isLiked);
        } catch {
            setPosts(prev => prev.map(p => {
                if (p.id !== postId) return p;
                const likes = { ...(p.likes || {}) };
                if (isLiked) {
                    likes[uid] = { nickname: nickname || '' };
                } else {
                    delete likes[uid];
                }
                return { ...p, likes, likeCount: Math.max(0, (p.likeCount || 0) + (isLiked ? 1 : -1)) };
            }));
            showToast?.('좋아요 처리에 실패했어요');
        }
    };

    const refreshFeed = async () => {
        cursorPublic.current = null;
        cursorFriends.current = null;
        hasMoreRef.current = true;
        setHasMore(true);
        await fetchMore(true);
        showToast?.('피드를 새로 불러왔어요');
    };

    const friendUids = new Set((friends || []).map(f => f.uid));
    const filteredPosts = posts.filter(p => {
        if (filter === 'me') return p.uid === uid;
        if (filter === 'friends') return friendUids.has(p.uid);
        return true;
    });

    const FILTERS = [
        { key: 'all', label: '전체' },
        { key: 'friends', label: '친구' },
        { key: 'me', label: '나' },
    ];

    return (
        <>
            <div className="feed-toolbar">
                <div className="feed-filter-row">
                    {FILTERS.map(f => (
                        <button
                            key={f.key}
                            className={`feed-filter-btn${filter === f.key ? ' active' : ''}`}
                            onClick={() => setFilter(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <button className="feed-refresh-btn" onClick={refreshFeed} disabled={loading}>
                    {loading ? '불러오는 중' : '새로고침'}
                </button>
            </div>

            {filteredPosts.map(p => (
                <PostCard key={p.id} post={p} myUid={uid} onLike={handleLike} onProfile={onProfile} />
            ))}
            {loading && (
                <div className="empty-state"><div className="empty-text">···</div></div>
            )}
            {!loading && filteredPosts.length === 0 && (
                <div className="empty-state">
                    <div className="empty-text">
                        {filter === 'me' && '아직 나의 피드 게시물이 없어요.'}
                        {filter === 'friends' && '아직 친구들의 게시물이 없어요.'}
                        {filter === 'all' && '아직 피드가 비어 있어요.\n운동을 기록하고 공개하면 여기에 보여요.'}
                    </div>
                </div>
            )}
            {!hasMore && filteredPosts.length > 0 && (
                <div style={{ textAlign: 'center', padding: '16px 0 8px', fontSize: 12, color: 'var(--text3)' }}>
                    모든 게시물을 봤어요
                </div>
            )}
            <div ref={sentinelRef} style={{ height: 1 }} />
        </>
    );
}

export default function Social({ user, nickname, records, friends, friendRequests, sentFriendRequests, addFriend, removeFriend, getFriendData, acceptFriendRequest, declineFriendRequest, cancelFriendRequest, showToast, userId, toggleLike }) {
    const [activeTab, setActiveTab] = useState('feed');
    const [profile, setProfile] = useState(null);
    const uid = user?.uid;

    return (
        <>
            <div className="social-inner-tab-bar">
                <button
                    className={`social-inner-tab ${activeTab === 'feed' ? 'active' : ''}`}
                    onClick={() => setActiveTab('feed')}
                >
                    피드
                </button>
                <button
                    className={`social-inner-tab ${activeTab === 'friends' ? 'active' : ''}`}
                    onClick={() => setActiveTab('friends')}
                >
                    친구
                </button>
            </div>

            {activeTab === 'feed' && uid && (
                <FeedTab uid={uid} nickname={nickname} friends={friends} toggleLike={toggleLike} onProfile={setProfile} showToast={showToast} />
            )}

            {activeTab === 'friends' && (
                <Friends
                    userId={userId}
                    user={user}
                    nickname={nickname}
                    records={records}
                    friends={friends}
                    friendRequests={friendRequests}
                    sentFriendRequests={sentFriendRequests}
                    addFriend={addFriend}
                    removeFriend={removeFriend}
                    getFriendData={getFriendData}
                    acceptFriendRequest={acceptFriendRequest}
                    declineFriendRequest={declineFriendRequest}
                    cancelFriendRequest={cancelFriendRequest}
                    showToast={showToast}
                    showBack={false}
                />
            )}

            {profile && (
                <ProfileModal
                    profile={profile}
                    myUid={uid}
                    friends={friends}
                    getFriendData={getFriendData}
                    addFriend={addFriend}
                    showToast={showToast}
                    onClose={() => setProfile(null)}
                />
            )}
        </>
    );
}
